/* eslint-disable react-hooks/exhaustive-deps */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import useFetch from '../hooks/useFetch';
import { ILandingPage } from '../models';
import { useUserContext } from './user.context';

interface IProps {
  children: JSX.Element;
}

interface IDataContext {
  data: {
    generalData: any;
    landingPageData: ILandingPage;
    [slug: string]: any;
  };
  setPage: React.Dispatch<React.SetStateAction<string>>;
  page: string;
  error: any;
}

const PAGE_URL = `${process.env.REACT_APP_API_URL}/page`;
const LANDING_PAGE_URL = `${process.env.REACT_APP_API_URL}/landing_page`;

const DataContext = createContext<any>(null);

export const useDataContext = () => useContext(DataContext) as IDataContext;

export function DataContextProvider({ children }: IProps) {
  const { host } = window.location;
  const subdomain = host.split('.')[0];
  const { result: landingPageData, doFetch: fetchLandingPage } = useFetch(
    `${LANDING_PAGE_URL}?subdomain=${subdomain}&populate=sso_connections`,
    { skipFailAlert: true },
  );
  const { user } = useUserContext();
  const { result: generalData, doFetch: fetchGeneralData } = useFetch(
    `${PAGE_URL}?slug=world-general-data&populate=img&world_page=true`,
  );
  const [page, setPage] = useState('');
  const { doFetch: fetchPageData, error } = useFetch(
    `${PAGE_URL}?slug=${page}&world_page=true&populate=user_id&populate=img&populate=article&populate=profile_pic&populate=authors&populate=tags&populate=case&populate=images`,
    { skipFailAlert: true },
  );

  const [data, setData] = useState<any>({
    generalData: {
      logo: { img: '' },
      navigation: {},
      legal: {},
      footer: {},
      contact: {},
    },
  });

  useEffect(() => {
    if (!subdomain) return;
    fetchLandingPage();
  }, [subdomain]);

  useEffect(() => {
    if (!landingPageData) return;
    setData((prevState: any) => ({
      ...prevState,
      landingPageData: landingPageData.data,
    }));
  }, [landingPageData]);

  useEffect(() => {
    fetchGeneralData();
  }, []);

  useEffect(() => {
    if (!generalData) return;
    setData((prevState: any) => ({
      ...prevState,
      generalData: generalData.data.body,
    }));
  }, [generalData]);

  useEffect(() => {
    if (data[page] || !page) return;
    const fetchData = async () => {
      const res = await fetchPageData({
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      if (res === 'aborted') return;
      if (res?.status === 'success') {
        setData((prevState: any) => ({
          ...prevState,
          [page]: { ...res.data.body, type: res.data.type },
        }));
      } else {
        setData((prevState: any) => ({
          ...prevState,
          [page]: 'Page not found',
        }));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPageData, page]);

  const value = useMemo(
    () => ({
      data,
      setPage,
      page,
      error,
    }),
    [data, error, page],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
